<template>
  <div
    :id="`${id}_wrap`"
    ref="fileUploadComponent"
    class="filedupload-wrap">
    <div
      class="fileupload-input-line"
      :class="error ? 'validation-error' : ''"
      ref="fileuploaddiv">
      <div class="fileupload-input">
        <button
          type="button"
          class="fileupload-input-button"
          aria-label="Choose files"
          @click.prevent="onButtonClick"
          @keydown.enter.prevent=""
          @keyup.enter.prevent="onButtonClick"
          @keydown.space.prevent=""
          @keyup.space.prevent="onButtonClick">
          <span class="fileupload-input-text">
            <svgicon
              original
              role="presentation"
              data="@iconPath/glyph/24/attach.svg?inline"
              width="24"
              height="24"></svgicon>
          </span>
        </button>
        <input
          multiple
          :name="`${name}`"
          :id="`${name}`"
          type="file"
          style="display: none"
          :data-rule-required="required"
          ref="fileInput"
          @click="validate"
          @change="onFileChange" />
        <div
          v-for="(attachedFile, index) in files"
          :key="index">
          <a
            class="fillin fileupload-input-text fileupload-input-multi no-overlay"
            :download="attachedFile.name"
            :href="
              requestFile
                ? 'messagecenter.html?action=ViewFile&BlobFileName=' +
                  attachedFile.name
                : fileLink[index]
            ">
            {{ attachedFile.name }}
          </a>
          <input
            v-if="allowUpdate && fileAttached"
            @click="removeFile(index)"
            type="button"
            class="icon-close"
            aria-label="Remove file" />
        </div>
      </div>
    </div>
    <div
      v-if="allowUpdate"
      class="fileupload-info"
      :class="error ? 'validation-error' : ''">
      <span ref="fileuploadExtensionInfo"
        >Safe extensions to upload: {{ uploadAllowedExtensions }}
      </span>
      <span ref="fileuploadSizeInfo"
        >Maximum size allowed: {{ maxSize }} MB/file, 25 MB/message
      </span>
      <span ref="fileuploadNoFiles"
        >Up to 5 files can be uploaded at a time. The file name should not
        contain special characters, including comma.</span
      >
    </div>
  </div>
</template>

<script>
export default {
  name: 'MultipleFileUploadComponent',
  emits: ['change'],
  props: {
    id: String,
    test: String,
    name: String,
    modelValue: Blob,
    label: String,
    errorMessage: String,
    required: String,
    uploadAllowedExtensions: {
      type: String,
      default: 'bmp,csv,doc,docx,jpg,pdf,png,rtf,txt,xls,xlsx,heic'
    },
    maxSize: {
      type: Number,
      default: 25
    },
    allowUpdate: {
      type: Boolean,
      default: false
    },
    blobFileNames: {
      type: Array,
      default: () => []
    },
    thumbImage: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      error: false,
      fileAttached: false,
      fileLink: [],
      files: [],
      localBlobFileNames: this.blobFileNames,
      localThumbImage: this.thumbImage,
      objectURL: [],
      requestFile: false,
      totalSize: 0
    }
  },
  created() {
    if (this.blobFileNames.length > 0) {
      this.fileAttached = true
      this.requestFile = true
    }
  },

  methods: {
    onButtonClick() {
      if (this.allowUpdate) {
        this.$refs.fileInput.click()
      }
    },

    validate() {
      if (this.required && this.files.length > 0) {
        this.error = true
      }
    },

    copyFileToInput() {
      var dataTransfer = new DataTransfer()
      for (let file of this.files) {
        dataTransfer.items.add(file)
      }
      this.$refs.fileInput.files = dataTransfer.files
    },

    onFileChange(event) {
      var files = event.target.files
      var actualLength,
        exists,
        element,
        file,
        fileExtension,
        actualSize = 0
      const imageExtensions = 'bmp,jpg,png'

      this.removeError()

      if (files) {
        for (file of files) {
          if (file.size && file.size > this.maxSize * 1024 * 1024) {
            this.error = true
            this.$refs.fileuploadSizeInfo.classList.add('error-message')
          }
          actualSize = actualSize + file.size
          if (file.name) {
            if (file.name.indexOf(',') !== -1) {
              this.$refs.fileuploadNoFiles.classList.add('error-message')
              this.error = true
              return
            }
            exists = this.localBlobFileNames.find(function (fileName) {
              return fileName === file.name
            })
            fileExtension = file.name.replace(/^.*\./, '').toLowerCase()
            if (!this.uploadAllowedExtensions.includes(fileExtension)) {
              this.error = true
              this.$refs.fileuploadExtensionInfo.classList.add('error-message')
            }
          }
        }
      } else {
        if (this.required && this.localBlobFileNames.length === 0) {
          this.error = true
          return
        }
      }

      if (this.error || exists) {
        this.copyFileToInput()
        return
      }

      actualLength = this.files.length > 0 ? this.files.length : 0
      if (actualLength + files.length > 5) {
        this.$refs.fileuploadNoFiles.classList.add('error-message')
        this.error = true
        return
      }
      if (this.totalSize + actualSize > 25 * 1024 * 1024) {
        this.$refs.fileuploadSizeInfo.classList.add('error-message')
        this.error = true
        return
      }
      this.totalSize = actualSize + this.totalSize
      this.files.push.apply(this.files, Array.from(files))

      if (this.files.length > 0) {
        this.fileAttached = true

        for (let i = actualLength; i < this.files.length; i++) {
          file = this.files[i]
          this.objectURL.push(URL.createObjectURL(file))
          this.localBlobFileNames.push(file.name) // this name is used when the user downloads the file
          this.fileLink.push(this.objectURL[i])

          if (file.name) {
            fileExtension = file.name.replace(/^.*\./, '').toLowerCase()
            if (imageExtensions.includes(fileExtension)) {
              this.localThumbImage.push({
                name: file.name,
                image: this.fileLink[i]
              })
            }
            this.$emit('change', file)
            element = document.createElement('input')
            element.type = 'file'
            element.name = this.name + file.name
            element.id = this.name + file.name
            element.style = 'display: none;'

            const dataTransfer = new DataTransfer()
            dataTransfer.items.add(file) //your file(s) reference(s)
            element.files = dataTransfer.files
            this.$refs.fileuploaddiv.appendChild(element)
          }
        }
      }
      this.copyFileToInput()
    },

    removeFile(index) {
      this.removeError()

      if (!this.allowUpdate) return
      this.totalSize = this.totalSize - this.files[index].size
      this.files.splice(index, 1)
      this.localThumbImage = this.localThumbImage.filter(
        (imageTh) => imageTh.name !== this.localBlobFileNames[index]
      )
      this.localBlobFileNames.splice(index, 1)
      this.fileLink.splice(index, 1)

      if (this.objectURL[index]) {
        // revoke the old object url to avoid using more memory than needed
        URL.revokeObjectURL(this.objectURL[index])
      }
      this.objectURL.splice(index, 1)

      this.validate()
      for (let file in this.files) {
        this.$emit('change', file)
      }
      this.removeElement(index)

      if (this.files.length === 0) {
        this.fileAttached = false
        this.$refs.fileInput.value = null
        this.$emit('change', null)
      }
    },

    removeError() {
      this.error = false
      this.$refs.fileuploadSizeInfo.classList.remove('error-message')
      this.$refs.fileuploadExtensionInfo.classList.remove('error-message')
      this.$refs.fileuploadNoFiles.classList.remove('error-message')
    },

    removeElement(index) {
      var attachments = this.$refs.fileInput.files
      var fileBuffer = new DataTransfer() // we need it to re-make the input selection

      // append the file list to an array iteratively
      for (let i = 0; i < attachments.length; i++) {
        // Exclude file in specified index
        if (index !== i) fileBuffer.items.add(attachments[i])
        else document.getElementById(this.name + attachments[i].name).remove() // we need to delete the file from the hidden input
      }

      // Assign buffer to file input
      this.$refs.fileInput.files = fileBuffer.files
    }
  }
}
</script>

<style lang="less">
@import '../assets/css/abstract/variables.less';
@import '../assets/css/abstract/mixins.less';

.filedupload-wrap {
  width: 100%;
  float: left;
  padding-top: 0 !important;

  .fileupload-input-line {
    border: 0 !important;
    border-radius: 0 !important;
  }

  .fileupload-input {
    width: auto !important;
    border-radius: 4px;

    @media @tablet-down {
      display: block !important;
      width: 100% !important;
    }

    button.fileupload-input-button {
      .default-focus-indicator(0, @primary-color);
      border-radius: 4px;
    }

    div {
      margin-right: 5px;
      border: 1px solid hsla(0, 0%, 75.3%, 0.5);
      border-radius: 4px;

      @media @tablet-down {
        width: 100%;
        margin-top: 5px;
        margin-right: 0;
        float: left;
      }
    }

    .fileupload-input-multi {
      overflow: hidden;
      max-width: 215px !important;
      white-space: nowrap;

      @media @tablet-down {
        max-width: calc(100% - 25px) !important;
      }
    }

    .icon-close {
      background-position: 7px 12px;
      width: 25px;
      height: 36px;
      float: right;
      border: 0;
      border-left: 1px solid hsla(0, 0%, 75.3%, 0.5);
    }
  }

  .fileupload-info {
    margin-top: 5px;
  }
}
</style>
